import { Observable, combineLatest, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { UserSettings, Workspace } from '../user-settings.dto';
import { PermissionsService } from './permissions.service';
import { AccessLevelEnum } from 'app/main/pages/users/roles/roles.service';
import { IUserResultT } from 'app/main/pages/contract-control/contract-control.permission.service';

export abstract class BasePermissionService {

    constructor(
        protected _permissionService: PermissionsService,
        protected isTenantOwner$: Observable<boolean>,
        protected defaultWorkSpace$: Observable<Workspace>,
        protected userSettings$: Observable<UserSettings>
    ) {}


    protected abstract getAccessLevel(id: number, docTypeId:number, categoryIds:number | number[]): Observable<AccessLevelEnum>;


    /**
     * Checks if the user has the required access level for a given resource.
     *
     * @param id - The ID of the resource.
     * @param result - The user result object containing user information.
     * @param requiredAccessLevel - The required access level to check against.
     * @returns An Observable that emits the access level if the user has the required permissions, or null if an error occurs.
     */
    verifyPermission(
        id: number,
        docTypeId:number,
        categoryIds:number | number[],
        result: IUserResultT
    ): Observable<AccessLevelEnum> {
        const createdByMe$ = this._permissionService.isCreatedByMe(result);
        const accessLevel$ = this.getAccessLevel(id, docTypeId, categoryIds);
        const parentCategoryIds$ = this._permissionService.getParentCategoriesIds(categoryIds);

        return combineLatest([
            accessLevel$, 
            createdByMe$, 
            this.isTenantOwner$, 
            this.defaultWorkSpace$, 
            this.userSettings$,
            parentCategoryIds$
        ]).pipe(
            map(([accessLevel, isCreatedByMe, isTenantOwner, defaultWorkSpace, userSettings, parentCategoryIds]) => {
                const hasRoleFullAccess = isTenantOwner || isCreatedByMe;
                const checkRoleAccessLevel = this.checkRolesAccessLevel(hasRoleFullAccess, accessLevel);
                return checkRoleAccessLevel && [AccessLevelEnum.personal, AccessLevelEnum.use_only, AccessLevelEnum.view].includes(checkRoleAccessLevel) === false 
                    ? checkRoleAccessLevel 
                    : this.checkGroupAccess(userSettings, result, defaultWorkSpace, id, docTypeId, categoryIds, parentCategoryIds);
            }),
            catchError((error) => {
                // @TODO - Debugging perpose will remove it after approval
                console.log('error in verifyPermission', error);
                return of(AccessLevelEnum.use_only)
            })
        );
    }

    protected abstract checkRolesAccessLevel(hasRoleFullAccess: boolean, accessLevel: AccessLevelEnum): AccessLevelEnum;
    protected abstract checkGroupAccess(userSettings: UserSettings, result: IUserResultT, defaultWorkSpace: Workspace, id: number, docTypeId: number, categoryIds: number | number[], parentCategoryIds: number[]): AccessLevelEnum;
}
